import React, {useEffect, useRef, useState} from "react";
import {ApiError} from "../../api";
import {getFinanceStatistics} from "../../api/admin/finance";
import type {FinanceMoney, FinanceStatistics, FinanceStatisticsRow} from "../../api/admin/finance";
import {useAuth} from "../../auth";
import {ErrorBanner} from "../../components";
import {DayRange, PERIOD_PRESETS, PeriodPicker, PeriodPreset, actionPeriod, presetRange} from "./FinanceActions";
import {formatFinanceAmount} from "./financeFormat";
import {financeToday} from "./financeTime";

// Статистика по заявкам, созданным за период: оборот, выполненные, средний чек
// и конверсия — по мерчантам (их заявки), по командам и трейдерам (их платежи).

const QUICK: readonly PeriodPreset[] = ["today", "yesterday", "7d", "30d", "month", "prev_month"];

const usdt = (value?: FinanceMoney): string => formatFinanceAmount(value);
const rub = (value?: FinanceMoney): string => formatFinanceAmount(value, {digits: 0});
const count = (n: number): string => n.toLocaleString("ru-RU");
const atomic = (value?: FinanceMoney): number => Number(value?.atomic || 0);
const percent = (share: number): string => `${(share * 100).toFixed(1).replace(".", ",")} %`;

// conversionRate — доля выполненных среди завершённых; заявки в работе не входят.
export function conversionRate(row: Pick<FinanceStatisticsRow, "completed" | "cancelled">): number | null {
    const closed = row.completed + row.cancelled;
    return closed ? row.completed / closed : null;
}

export function conversion(row: Pick<FinanceStatisticsRow, "completed" | "cancelled">): string {
    const rate = conversionRate(row);
    return rate === null ? "—" : percent(rate);
}

// conversionTone — цвет конверсии: от 70 % хорошо, от 50 % средне, ниже — плохо.
export function conversionTone(rate: number | null): "good" | "mid" | "low" | "none" {
    if (rate === null) return "none";
    if (rate >= 0.7) return "good";
    return rate >= 0.5 ? "mid" : "low";
}

export function StatisticsPage(): JSX.Element {
    const {token} = useAuth();
    const today = financeToday();
    const [range, setRange] = useState<DayRange>(() => presetRange("month", financeToday()));
    const [stats, setStats] = useState<FinanceStatistics | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [refresh, setRefresh] = useState(0);
    const [crew, setCrew] = useState<"teams" | "traders">("teams");
    const seq = useRef(0);

    useEffect(() => {
        if (!token) return;
        const current = ++seq.current;
        let period: {from: string; to: string};
        try { period = actionPeriod(range); } catch (err) { setError(err); setStats(null); setLoading(false); return; }
        setLoading(true); setError(null);
        getFinanceStatistics(token, period)
            .then((result) => { if (current === seq.current) setStats(result); })
            .catch((err) => { if (current === seq.current) { setError(err); setStats(null); } })
            .finally(() => { if (current === seq.current) setLoading(false); });
    }, [token, range, refresh]);

    useEffect(() => { const current = seq; return () => { ++current.current; }; }, []);

    const sameRange = (other: DayRange): boolean => other.start === range.start && other.end === range.end;
    return <div className="page finance-page">
        <div className="page-header">
            <div><h2>Statistics</h2><p className="finance-subtitle">Заявки, созданные за период: оборот, выполненные и конверсия по мерчантам, командам и трейдерам.</p></div>
            <div className="finance-header-actions"><button className="btn btn-ghost btn-sm" onClick={() => setRefresh((n) => n + 1)} disabled={loading}>{loading ? "Обновление..." : "Обновить"}</button></div>
        </div>
        <p className="finance-wallet-hint">Все даты: Москва (UTC+3). Первый и последний день периода входят целиком; сегодняшний — до текущего момента.</p>
        <div className="fa">
            <div className="fa-period">
                <div className="fa-presets" role="group" aria-label="Быстрый выбор периода">
                    {QUICK.map((key) => { const preset = presetRange(key, today); return <button type="button" key={key} className={sameRange(preset) ? "on" : ""} aria-pressed={sameRange(preset)} onClick={() => setRange(preset)}>{PERIOD_PRESETS.find((p) => p.key === key)?.label}</button>; })}
                </div>
                <PeriodPicker value={range} today={today} onChange={setRange}/>
            </div>
            <ErrorBanner error={error} title={error instanceof ApiError && error.status === 403 ? "Нет доступа к статистике" : "Не удалось загрузить статистику"}/>
            {!stats && loading && <div className="fa-tiles st-tiles" aria-busy="true" aria-label="Загрузка итогов">{[0, 1, 2, 3].map((i) => <div className="fa-tile fa-tile-skeleton" key={i}/>)}</div>}
            {stats && <>
                <StatisticsTiles total={stats.merchant_total} busy={loading}/>
                <StatisticsTable title="Мерчанты" who="Мерчант" kind="m" rows={stats.merchants} total={stats.merchant_total} average busy={loading}/>
                <StatisticsTable title="Команды и трейдеры" who={crew === "teams" ? "Команда" : "Трейдер"} kind="t" rows={(crew === "teams" ? stats.teams : stats.traders) || []} total={stats.trader_total} busy={loading}
                    toolbar={<div className="fa-seg" role="group" aria-label="Команды или трейдеры">
                        {([["teams", "Команды", stats.teams], ["traders", "Трейдеры", stats.traders]] as const).map(([key, label, rows]) =>
                            <button type="button" key={key} className={crew === key ? "on" : ""} aria-pressed={crew === key} onClick={() => setCrew(key)}>{label}<span>{(rows || []).length}</span></button>)}
                    </div>}/>
                <div className="fa-foot"><span>Оборот — сумма успешных платежей; USDT — по курсу платежа, как в проводке баланса. Полоска под именем — доля в обороте ₽ за период. Мерчант считается по заявкам, трейдер — по своим платежам: заявка, которую трейдер отпустил, — его отмена. Команда — сумма платежей своих трейдеров. Конверсия — выполненные из выполненных и отменённых; заявки в работе не входят. Цвет: от 70 % — зелёный, от 50 % — жёлтый, ниже — красный. Таблицы сортируются по клику на заголовок.</span></div>
            </>}
        </div>
    </div>;
}

function StatisticsTiles({total, busy}: {total: FinanceStatisticsRow; busy: boolean}): JSX.Element {
    const rate = conversionRate(total);
    return <div className={`fa-tiles st-tiles${busy ? " fa-busy" : ""}`} aria-label="Итоги за период">
        <div className="fa-tile st-tile-turnover">
            <span className="fa-cap">Оборот</span>
            <strong>{usdt(total.turnover_usdt)}<i>USDT</i></strong>
            <span className="fa-sub">{rub(total.turnover_rub)} ₽</span>
        </div>
        <div className="fa-tile st-tile-done">
            <span className="fa-cap">Выполнено заявок</span>
            <strong>{count(total.completed)}</strong>
            <span className="fa-sub">отменено {count(total.cancelled)}</span>
        </div>
        <div className={`fa-tile st-tile-conv st-tone-${conversionTone(rate)}`}>
            <span className="fa-cap">Конверсия</span>
            <strong>{conversion(total)}</strong>
            <span className="st-meter" aria-hidden="true"><i style={{width: `${(rate || 0) * 100}%`}}/></span>
        </div>
        <div className="fa-tile st-tile-avg">
            <span className="fa-cap">Средний чек</span>
            <strong>{total.average_rub ? rub(total.average_rub) : "—"}<i>₽</i></strong>
            <span className="fa-hint">оборот ₽ на выполненную заявку</span>
        </div>
    </div>;
}

type SortKey = "name" | "usdt" | "rub" | "completed" | "average" | "conversion";
type Sort = {key: SortKey; desc: boolean};

const sortValue: Record<Exclude<SortKey, "name">, (row: FinanceStatisticsRow) => number> = {
    usdt: (row) => atomic(row.turnover_usdt),
    rub: (row) => atomic(row.turnover_rub),
    completed: (row) => row.completed,
    average: (row) => (row.average_rub ? atomic(row.average_rub) : -1),
    conversion: (row) => conversionRate(row) ?? -1,
};

const rowName = (row: FinanceStatisticsRow): string => row.owner.name || row.owner.id || (row.owner.type === "team" ? "Без команды" : "—");

export function sortStatistics(rows: FinanceStatisticsRow[], sort: Sort): FinanceStatisticsRow[] {
    const sign = sort.desc ? -1 : 1;
    return [...rows].sort((a, b) => {
        const by = sort.key === "name" ? rowName(a).localeCompare(rowName(b), "ru") : sortValue[sort.key](a) - sortValue[sort.key](b);
        return by * sign || atomic(b.turnover_rub) - atomic(a.turnover_rub) || rowName(a).localeCompare(rowName(b), "ru");
    });
}

function StatisticsTable({title, who, kind, rows, total, average = false, busy, toolbar}: {
    title: string; who: string; kind: "m" | "t"; rows: FinanceStatisticsRow[]; total: FinanceStatisticsRow; average?: boolean; busy: boolean; toolbar?: React.ReactNode;
}): JSX.Element {
    const [sort, setSort] = useState<Sort>({key: "rub", desc: true});
    const columns = average ? 6 : 5;
    const totalRub = atomic(total.turnover_rub);
    const head = (key: SortKey, label: string, num = true): JSX.Element => {
        const on = sort.key === key;
        return <th className={`${num ? "fa-num " : ""}st-sort${on ? " on" : ""}`} aria-sort={on ? (sort.desc ? "descending" : "ascending") : "none"}>
            <button type="button" onClick={() => setSort(on ? {key, desc: !sort.desc} : {key, desc: key !== "name"})}>{label}</button>
        </th>;
    };
    const cells = (row: FinanceStatisticsRow): JSX.Element => {
        const rate = conversionRate(row);
        return <>
            <td data-l="Оборот, USDT" className="mono fa-num">{usdt(row.turnover_usdt)}</td>
            <td data-l="Оборот, ₽" className="mono fa-num">{rub(row.turnover_rub)}</td>
            <td data-l="Выполнено" className="mono fa-num">{count(row.completed)}</td>
            {average && <td data-l="Средний чек, ₽" className="mono fa-num">{row.average_rub ? rub(row.average_rub) : "—"}</td>}
            <td data-l="Конверсия" className="fa-num"><span className={`st-conv st-tone-${conversionTone(rate)}`}>{conversion(row)}</span><small>отменено {count(row.cancelled)}</small></td>
        </>;
    };
    return <section className={`st-section st-kind-${kind}`} aria-label={title}>
        <div className="st-head"><h3>{title}{!toolbar && <span className="st-count">{rows.length}</span>}</h3>{toolbar}</div>
        <div className={`table-wrapper st-table${busy ? " fa-busy" : ""}`} aria-busy={busy}><table>
            <thead><tr>{head("name", who, false)}{head("usdt", "Оборот, USDT")}{head("rub", "Оборот, ₽")}{head("completed", "Выполнено")}{average && head("average", "Средний чек, ₽")}{head("conversion", "Конверсия")}</tr></thead>
            <tbody>{rows.length ? sortStatistics(rows, sort).map((row) => {
                const share = totalRub > 0 ? atomic(row.turnover_rub) / totalRub : 0;
                return <tr className="st-row" key={row.owner.id || "none"}>
                    <td data-l={who}>
                        <b>{rowName(row)}</b>
                        <span className="st-share" title={`Доля в обороте ₽: ${percent(share)}`} aria-hidden="true"><span><i style={{width: `${share * 100}%`}}/></span><em>{percent(share)}</em></span>
                    </td>
                    {cells(row)}
                </tr>;
            }) : <tr><td colSpan={columns}><div className="empty-state">За период заявок нет</div></td></tr>}</tbody>
            {rows.length > 0 && <tfoot><tr className="st-row st-total"><td data-l={who}><b>Итого</b></td>{cells(total)}</tr></tfoot>}
        </table></div>
    </section>;
}
